import * as React from "react";
import { useState } from "react";
import {
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Box, IconButton,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { StatusChip } from "./StatusChip";
import { WeeklyItem } from "@AMIEWEB/NewOrder/types";
import { useTranslation } from "react-i18next";
import { ScenarioHistoryModal } from "./ScenarioHistoryModal";

export interface ShiftTableProps { rows: WeeklyItem[]; }

/**
 * Returns the environment-specific base URL for job details
 * Based on REACT_APP_ENV: DEV, QA, INT, or PROD
 */
const getJobDetailsBaseUrl = (): string => {
  const env = globalThis?.app?.env?.REACT_APP_ENV;
  
  switch (env) {
    case 'DEV':
      return 'https://int.amnhealthcare.com';
    case 'QA':
      return 'https://int.amnhealthcare.com';
    case 'INT':
      return 'https://uat.amnhealthcare.com';
    case 'PROD':
    default:
      return 'https://www.amnhealthcare.com';
  }
};

export const ShiftTable: React.FC<ShiftTableProps> = ({ rows }) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(null);

  const handleInfoClick = (scenarioId: number | null | undefined, event: React.MouseEvent) => {
    event.stopPropagation();
    if (scenarioId) {
      setSelectedScenarioId(scenarioId);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedScenarioId(null);
  };

  return (
    <>
    <TableContainer sx={{ width: 1, minWidth: 0 }}>
      <Table
        size="small"
        aria-label="weekly gross pay"
        sx={{
          width: 1,                 
          tableLayout: "auto",      
          borderCollapse: "separate",
          "& th, & td": {
            px: 1.5,
            py: 0.75,
            lineHeight: 1.3,
            fontSize: 13,
            textAlign: "left",
            verticalAlign: "middle",
            wordBreak: "break-word",   
          },
          "& thead th": {
            whiteSpace: "nowrap",     
            fontWeight: 600,
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            bgcolor: (t) => t.palette.grey[100],
            color: "text.secondary",
          },
          "& tbody td": {
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
          },
          "& tbody tr:last-of-type td": { borderBottom: 0 },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('order.orderDetails.weeklyGrossPay.shift')}</TableCell>
            <TableCell>{t('order.orderDetails.weeklyGrossPay.mrh')}</TableCell>
            <TableCell>{t('order.orderDetails.weeklyGrossPay.weeklyGross')}</TableCell>
            <TableCell>{t('order.orderDetails.weeklyGrossPay.payRange')}</TableCell>
            <TableCell>{t('order.orderDetails.weeklyGrossPay.status')}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.orderId} hover>
              <TableCell sx={{ fontWeight: 500 }}>{r.shift}</TableCell>
              <TableCell>{r.MRH}</TableCell>
              <TableCell>{r.WeeklyGross}</TableCell>
              <TableCell>{r.PayRange}</TableCell>
              <TableCell>
                <Box
                  component="span"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      cursor:
                        r.jdhPostingStatus === "Success" && r.jdhPostingId
                          ? "pointer"
                          : "default",
                    }}
                    onClick={() => {
                      if (r.jdhPostingStatus === "Success" && r.jdhPostingId) {
                        const id = String(r.jdhPostingId).trim();
                        if (id) {
                          const baseUrl = getJobDetailsBaseUrl();
                          window.open(
                            `${baseUrl}/job-details/${id}/`,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      }
                    }}
                  >
                    <StatusChip
                      status={r.status}
                      clickable={r.jdhPostingStatus === "Success" && !!r.jdhPostingId}
                    />
                  </Box>
                  {r.status === "Posted" && r.scenarioId && (
                    <IconButton
                      size="small"
                      onClick={(e) => handleInfoClick(r.scenarioId, e)}
                      sx={{
                        color: "#1976d2",
                        padding: 0.5,
                        "&:hover": {
                          backgroundColor: "rgba(25, 118, 210, 0.1)",
                        },
                      }}
                    >
                      <InfoIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
    {selectedScenarioId && (
      <ScenarioHistoryModal
        open={modalOpen}
        onClose={handleCloseModal}
        scenarioId={selectedScenarioId}
      />
    )}
    </>
  );
};
